import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Printer, X } from 'lucide-react';
import {
    downloadPeProfitPdf,
    generatePeProfitPdfBlob,
    PE_PROFIT_PDF_FILENAME,
    printPeProfitPdfBlob,
} from './peProfitPdfExport';

interface PEProfitPdfPreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const PEProfitPdfPreviewModal: React.FC<PEProfitPdfPreviewModalProps> = ({ isOpen, onClose }) => {
    const previewRef = useRef<HTMLDivElement>(null);
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    /** Druck & Download nutzen denselben html2canvas/jsPDF-Pfad — kein CSS-Print-Split mehr. */
    const buildPdfBlob = useCallback(async (): Promise<Blob | null> => {
        const source = document.getElementById('pe-profit-report-export');
        if (!source) {
            setError('Report konnte nicht gefunden werden.');
            return null;
        }
        return generatePeProfitPdfBlob(source);
    }, []);

    const handlePrint = useCallback(async () => {
        setIsBusy(true);
        setError(null);
        try {
            const blob = await buildPdfBlob();
            if (!blob) return;
            printPeProfitPdfBlob(blob);
        } catch (printError) {
            const message =
                printError instanceof Error
                    ? printError.message
                    : 'Druckvorschau konnte nicht erstellt werden.';
            setError(message);
        } finally {
            setIsBusy(false);
        }
    }, [buildPdfBlob]);

    const handleDownload = useCallback(async () => {
        setIsBusy(true);
        setError(null);

        try {
            const blob = await buildPdfBlob();
            if (!blob) return;
            downloadPeProfitPdf(blob, PE_PROFIT_PDF_FILENAME);
        } catch (downloadError) {
            const message =
                downloadError instanceof Error
                    ? downloadError.message
                    : 'PDF konnte nicht erstellt werden.';
            setError(message);
        } finally {
            setIsBusy(false);
        }
    }, [buildPdfBlob]);

    useEffect(() => {
        if (!isOpen) {
            setError(null);
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, handleClose]);

    useEffect(() => {
        if (!isOpen) return;

        const source = document.getElementById('pe-profit-report-export');
        const container = previewRef.current;
        if (!source || !container) return;

        container.replaceChildren();
        const clone = source.cloneNode(true) as HTMLElement;
        clone.removeAttribute('id');
        clone.classList.add('pe-profit-export-mode');
        container.appendChild(clone);
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className="pe-profit-no-print fixed inset-0 z-[200] flex items-center justify-center bg-stone-900/70 dark:bg-black/80 p-4 backdrop-blur-sm"
            onClick={handleClose}
            role="presentation"
        >
            <div
                className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-pe-border-strong dark:border-[#333] bg-pe-card dark:bg-[#111] shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pe-profit-pdf-preview-title"
            >
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-pe-border dark:border-[#2a2a2a] px-5 py-4">
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]/80">
                            PDF-Vorschau
                        </p>
                        <h3 id="pe-profit-pdf-preview-title" className="text-lg font-bold text-pe-text dark:text-white">
                            Rentabilitätsvorschau prüfen &amp; speichern
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-pe-border-strong dark:border-slate-600 bg-pe-elevated dark:bg-slate-800 px-4 py-2 text-sm font-bold uppercase tracking-wider text-pe-text dark:text-slate-200 hover:bg-pe-hover dark:hover:bg-slate-700"
                    >
                        <X size={16} />
                        Schließen
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto bg-slate-200 p-4">
                    {error && (
                        <div className="mb-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-700">
                            {error}
                        </div>
                    )}
                    <div
                        ref={previewRef}
                        className="mx-auto max-w-4xl rounded-lg bg-white shadow-lg"
                    />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-pe-border dark:border-[#2a2a2a] px-5 py-4">
                    <p className="max-w-md text-xs text-stone-500 dark:text-slate-500">
                        Drucken und PDF nutzen denselben Export — Pagination wie in der heruntergeladenen Datei.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => void handleDownload()}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-4 py-2 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Download size={16} />
                            {isBusy ? 'PDF wird erstellt …' : 'PDF herunterladen'}
                        </button>
                        <button
                            type="button"
                            onClick={() => void handlePrint()}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 rounded-lg border border-pe-border-strong dark:border-slate-600 bg-pe-elevated dark:bg-slate-800 px-4 py-2 text-sm font-bold uppercase tracking-wider text-pe-text dark:text-slate-200 hover:bg-pe-hover dark:hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Printer size={16} />
                            Drucken
                        </button>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="inline-flex items-center gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-sm font-bold uppercase tracking-wider text-rose-300"
                        >
                            <X size={16} />
                            Schließen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PEProfitPdfPreviewModal;
